"use client";

import React, { useState } from "react";
import { IBM_Plex_Serif } from 'next/font/google';
import { useTranslations, getLocale } from "@/lib/i18n-utils";
import LandingNav from "@/app/(marketing)/_components/landing-nav";
import LandingFooter from '@/components/LandingFooter';

const ibmPlexSerif = IBM_Plex_Serif({ subsets: ['latin'], weight: ['300','400','600','700'], display: 'swap' });

const PrivacyPage: React.FC = () => {
  const [lang, setLang] = useState<"en" | "da" | "both">("both");

  // Initialize lang from stored locale (localStorage / cookie)
  React.useEffect(() => {
    const stored = getLocale();
    if (stored === "en" || stored === "da") setLang(stored);
    else setLang("both");
  }, []);

  const setLanguage = (l: "en" | "da" | "both") => {
    setLang(l);
    // update global locale for other components that use useTranslations()
    try {
      if (typeof window !== "undefined") {
        if (l === "both") {
          localStorage.removeItem("locale");
          // notify listeners with current stored locale (or default)
          const current = getLocale();
          window.dispatchEvent(new CustomEvent("localeChange", { detail: current }));
        } else {
          localStorage.setItem("locale", l);
          window.dispatchEvent(new CustomEvent("localeChange", { detail: l }));
        }
      }
    } catch (e) {
      // ignore storage errors
    }
  };

  const { locale } = useTranslations();

  return (
    <div className={`${ibmPlexSerif.className} root ${lang === "en" ? "show-en" : ""} ${lang === "da" ? "show-da" : ""}`}>
      <style>{`
        :root {
          --brand: #304B43;
          --brand-light: #EEF6F2;
          --brand-mid: #C9E1DA;
          --text: #1E293B;
          --muted: #64748B;
          --border: #E2E8F0;
          --bg: #FFFFFF;
          --section-bg: #F8FAFC;
        }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Segoe UI', system-ui, -apple-system, sans-serif; }
        .lang-bar { background: var(--brand); color: #fff; display: flex; justify-content: flex-end; align-items: center; gap: 8px; padding: 10px 40px; font-size: 14px; }
        .lang-bar span { opacity: .7; }
        .lang-btn { background: rgba(255,255,255,.15); border: 1px solid rgba(255,255,255,.3); color: #fff; padding: 4px 14px; border-radius: 20px; cursor: pointer; font-size: 13px; transition: background .2s; }
        .lang-btn.active, .lang-btn:hover { background: rgba(255,255,255,.35); }
        header { background: linear-gradient(135deg, var(--brand) 0%, #25443b 100%); color: #fff; padding: 48px 40px 24px; text-align: center; }
        header .logo { font-size: 32px; font-weight: 800; letter-spacing: -1px; margin-bottom: 12px; }
        header .logo span { opacity: .7; }
        header h1 { font-size: 22px; font-weight: 400; opacity: .9; margin-bottom: 8px; }
        header .updated { font-size: 13px; opacity: .65; }
        .wrapper { max-width: 860px; margin: 0 auto; padding: 0 24px 80px; }
        .toc { background: var(--brand-light); border: 1px solid var(--brand-mid); border-radius: 12px; padding: 28px 32px; margin: 8px 0 48px; }
        .toc h2 { font-size: 15px; text-transform: uppercase; letter-spacing: .06em; color: var(--brand); margin-bottom: 14px; }
        .toc ol { padding-left: 20px; }
        .toc li { margin-bottom: 5px; }
        .toc a { color: var(--brand); text-decoration: none; font-size: 15px; }
        .toc a:hover { text-decoration: underline; }
        section { margin-bottom: 52px; scroll-margin-top: 24px; }
        .section-label { display: inline-block; background: var(--brand); color: #fff; font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: .08em; padding: 3px 10px; border-radius: 4px; margin-bottom: 10px; }
        h2 { font-size: 22px; font-weight: 700; color: var(--text); margin-bottom: 6px; line-height: 1.3; }
        h3 { font-size: 17px; font-weight: 600; color: var(--brand); margin: 28px 0 10px; }
        p { margin-bottom: 14px; color: #334155; }
        ul, ol { padding-left: 22px; margin-bottom: 14px; }
        li { margin-bottom: 6px; color: #334155; }
        .card { background: var(--section-bg); border: 1px solid var(--border); border-left: 4px solid var(--brand); border-radius: 8px; padding: 20px 24px; margin-bottom: 20px; }
        .card p:last-child { margin-bottom: 0; }
        .contact-box { background: var(--brand-light); border: 1px solid var(--brand-mid); border-radius: 12px; padding: 28px 32px; text-align: center; }
        .contact-box a { color: var(--brand); font-weight: 600; font-size: 18px; text-decoration: none; }
        .contact-box a:hover { text-decoration: underline; }
        .rights-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 16px; }
        .right-item { background: var(--section-bg); border: 1px solid var(--border); border-radius: 8px; padding: 14px 18px; font-size: 15px; color: #334155; display: flex; align-items: flex-start; gap: 10px; }
        .right-item::before { content: "✓"; color: var(--brand); font-weight: 700; flex-shrink: 0; margin-top: 1px; }
        hr { border: none; border-top: 1px solid var(--border); margin: 60px 0; }
        .en, .da { display: block; }
        .root.show-da .en { display: none; }
        .root.show-en .da { display: none; }
        @media (max-width: 600px) { header { padding: 40px 20px 32px; } .lang-bar { padding: 10px 20px; } .rights-grid { grid-template-columns: 1fr; } .toc { padding: 20px; } h2 { font-size: 19px; } }
      `}</style>

     

      <section className="relative overflow-hidden">
        <LandingNav lang={locale as "da" | "en"} />
        

        <header className="relative z-10 mx-auto w-full max-w-7xl px-4 text-center">
          <div className="logo mx-auto">Sun<span>dok</span></div>
          <h1 className="en mb-2 w-full max-w-4xl mx-auto font-serif text-xl font-black leading-tight tracking-tight text-slate-900 sm:text-xl md:text-xl lg:text-[2.5rem] lg:leading-[1.05]">Privacy Policy</h1>
          <h1 className="da mb-2 w-full max-w-4xl mx-auto font-serif text-xl font-black leading-tight tracking-tight  sm:text-xl md:text-xl lg:text-[2.5rem] lg:leading-[1.05] text-white">Privatlivspolitik</h1>
          <p className="updated text-white">Last updated / Senest opdateret: April 2026</p>
        </header>
      </section>

      <div className="wrapper">
        <div className="en">
          <div className="toc">
            <h2>Contents</h2>
            <ol>
              <li><a href="#en-intro">Introduction</a></li>
              <li><a href="#en-partners">For Our Business Partners</a></li>
              <li><a href="#en-prospects">For Potential Customers & Partners</a></li>
              <li><a href="#en-sharing">Who We Share Your Data With</a></li>
              <li><a href="#en-storage">Where Your Data Is Stored</a></li>
              <li><a href="#en-rights">Your Rights</a></li>
              <li><a href="#en-security">Data Security</a></li>
              <li><a href="#en-contact">Contact Us</a></li>
              <li><a href="#en-changes">Changes to This Policy</a></li>
            </ol>
          </div>

          <section id="en-intro">
            <div className="section-label">Introduction</div>
            <h2>Your privacy matters to us</h2>
            <p>Svendetid is committed to protecting personal data and safeguarding the privacy of visitors to our website, as well as contact persons representing our customers, suppliers, and other current or potential partners.</p>
            <p>This Privacy Policy describes how Svendetid, acting as data controller, processes personal data in accordance with the General Data Protection Regulation (EU Regulation 2016/679, "GDPR").</p>
            <p>Sections 1–2 cover specific processing activities for business partners and prospective partners respectively. Sections 3–9 apply to both groups and cover sharing, storage, your rights, security, cookies, contact details, and policy updates.</p>
          </section>

          <section id="en-partners">
            <div className="section-label">Section 1</div>
            <h2>For Our Business Partners</h2>
            <h3>Contract Performance</h3>
            <div className="card">
              <p>If you represent a company we work with, we process personal data to support the business relationship, including communication about products and services.</p>
              <p><strong>Legal basis:</strong> Contract performance and our legitimate interest in conducting business.</p>
              <p><strong>Data we may process:</strong> name, contact details, job title, onboarding information for user access, IT-related data (username, system and application logs, metadata), email correspondence, and other communication data.</p>
              <p><strong>Retention:</strong> For the duration of the business relationship and up to three (3) years thereafter to facilitate potential re-engagement. Certain data may be retained longer for legal reasons.</p>
            </div>

            <h3>Customer Support</h3>
            <div className="card">
              <p>When you contact our support team, we process information such as name, company, email, phone number, role, profile image (if provided), and case data.</p>
              <p><strong>Purpose:</strong> To communicate with you and document the handling of support requests.</p>
              <p><strong>Legal basis:</strong> Our legitimate interest in providing customer service and improving our business.</p>
              <p><strong>Retention:</strong> For the duration of the contract period and a short period thereafter.</p>
            </div>

            <h3>Marketing</h3>
            <div className="card">
              <p>We market our products to current and potential customers. If you represent a company we work with or wish to work with, we may process your contact details for marketing purposes via email, SMS, or social media (e.g. LinkedIn).</p>
              <p><strong>Data we may process:</strong> preferences, device data, IP address, and browsing behaviour — used to tailor our communications.</p>
              <p>We do not use automated decision-making. You may opt out of profiling at any time by contacting us.</p>
              <p><strong>Retention:</strong> Until you unsubscribe, or up to one (1) year after your last activity.</p>
            </div>

            <h3>Legal Obligations</h3>
            <div className="card">
              <p>We may process personal data to comply with accounting, financial, and data protection legislation.</p>
              <p><strong>Legal basis:</strong> Legal obligation and our legitimate interest in documenting handled requests.</p>
              <p><strong>Retention:</strong> For the period required by applicable law.</p>
            </div>

            <h3>Legal Claims</h3>
            <div className="card">
              <p>In connection with any disputes, we may process personal data to establish, exercise, or defend legal claims.</p>
              <p><strong>Legal basis:</strong> Our legitimate interest in asserting or defending legal claims.</p>
              <p><strong>Retention:</strong> Up to ten (10) years, but never longer than necessary.</p>
            </div>
          </section>

          <section id="en-prospects">
            <div className="section-label">Section 2</div>
            <h2>For Potential Customers & Partners</h2>
            <h3>Marketing</h3>
            <div className="card">
              <p>Like other businesses, we have a legitimate interest in marketing our products to companies we wish to partner with. We may collect information from you, your company, or public sources (e.g. company registers) and data suppliers.</p>
              <p>We may contact you via email, SMS, or social media (e.g. LinkedIn).</p>
              <p><strong>Data we may process:</strong> preferences, device data, IP address, and browsing behaviour, to target our communications effectively.</p>
              <p><strong>Retention:</strong> For as long as relevant, and up to two (2) years after the most recent contact. If contact develops into a partnership, data will thereafter be handled as described under Section 1.</p>
            </div>

            <h3>Legal Claims</h3>
            <div className="card">
              <p>In connection with any disputes, we may process personal data to establish, exercise, or defend legal claims.</p>
              <p><strong>Retention:</strong> Up to ten (10) years, but never longer than necessary.</p>
            </div>
          </section>

          <section id="en-sharing">
            <div className="section-label">Section 3</div>
            <h2>Who We Share Your Data With</h2>
            <p>Svendetid may use external service providers acting as data processors — for example for hosting, support, marketing, and IT operations. These providers may access and process your personal data but are contractually obligated to protect it and act only on our documented instructions.</p>
            <p>We do not sell your personal data to third parties.</p>
          </section>

          <section id="en-storage">
            <div className="section-label">Section 4</div>
            <h2>Where Your Data Is Stored</h2>
            <p>We strive to store personal data within the EU/EEA. If data is transferred outside the EU/EEA, we ensure an adequate level of protection through the European Commission's Standard Contractual Clauses or other approved transfer mechanisms in accordance with GDPR Chapter V.</p>
          </section>

          <section id="en-rights">
            <div className="section-label">Section 5</div>
            <h2>Your Rights</h2>
            <p>As a data subject you have the following rights under GDPR. To exercise any of them, please contact us (see Section 8).</p>
            <div className="rights-grid">
              <div className="right-item">Right of access to the data we hold about you</div>
              <div className="right-item">Right to rectification of inaccurate or incomplete data</div>
              <div className="right-item">Right to erasure ("right to be forgotten") where there is no lawful basis for retention</div>
              <div className="right-item">Right to restriction of processing in certain circumstances</div>
              <div className="right-item">Right to data portability</div>
              <div className="right-item">Right to object to processing based on legitimate interests</div>
              <div className="right-item">Right to withdraw consent where processing is consent-based</div>
              <div className="right-item">Right to lodge a complaint with a supervisory authority</div>
            </div>
            <p style={{ marginTop: 18 }}>If you wish to file a complaint, you may contact the Danish Data Protection Authority: <a href="https://www.datatilsynet.dk" style={{ color: "var(--brand)" }}>www.datatilsynet.dk</a></p>
          </section>

          <section id="en-security">
            <div className="section-label">Section 6</div>
            <h2>Data Security</h2>
            <p>Svendetid implements appropriate technical and organisational security measures to protect your personal data against unauthorised access, disclosure, alteration, or destruction. These measures are reviewed and updated regularly in line with current best practices.</p>
          </section>

          <section id="en-contact">
            <div className="section-label">Section 7</div>
            <h2>Contact Us</h2>
            <div className="contact-box">
              <p style={{ marginBottom: 10, color: "#334155" }}>Questions about this policy or want to exercise your rights?</p>
              <a href="mailto:privacy@svendetid.com">privacy@svendetid.com</a>
              <p style={{ marginTop: 10, fontSize: 14, color: "var(--muted)" }}>Svendetid · Denmark</p>
            </div>
          </section>

          <section id="en-changes">
            <div className="section-label">Section 8</div>
            <h2>Changes to This Policy</h2>
            <p>Svendetid reserves the right to amend this Privacy Policy at any time. The date of the most recent revision will appear at the top of this page. For material changes we will notify you through the platform or by email where appropriate.</p>
          </section>
        </div>

        <hr className="en da" style={{ display: "block" }} />

        <div className="da">
          <div className="toc">
            <h2>Indhold</h2>
            <ol>
              <li><a href="#da-intro">Introduktion</a></li>
              <li><a href="#da-partners">For Vores Forretningspartnere</a></li>
              <li><a href="#da-prospects">For Potentielle Kunder &amp; Samarbejdspartnere</a></li>
              <li><a href="#da-sharing">Hvem Deler Vi Dine Oplysninger Med?</a></li>
              <li><a href="#da-storage">Hvor Opbevares Dine Oplysninger?</a></li>
              <li><a href="#da-rights">Dine Rettigheder</a></li>
              <li><a href="#da-security">Datasikkerhed</a></li>
              <li><a href="#da-contact">Kontakt Os</a></li>
              <li><a href="#da-changes">Ændringer i Politikken</a></li>
            </ol>
          </div>

          <section id="da-intro">
            <div className="section-label">Introduktion</div>
            <h2>Dit privatliv er vigtigt for os</h2>
            <p>Svendetid er forpligtet til at beskytte personoplysninger og værne om privatlivet for besøgende på vores hjemmeside samt for kontaktpersoner, der repræsenterer vores kunder, leverandører og andre nuværende eller potentielle samarbejdspartnere.</p>
            <p>Denne privatlivspolitik beskriver, hvordan Svendetid, i sin egenskab af dataansvarlig, behandler personoplysninger i overensstemmelse med Databeskyttelsesforordningen (EU Forordning 2016/679, "GDPR").</p>
            <p>Afsnit 1–2 beskriver behandling af personoplysninger for henholdsvis forretningspartnere og potentielle samarbejdspartnere. Afsnit 3–9 gælder for begge grupper og omhandler deling, opbevaring, dine rettigheder, sikkerhed, cookies, kontaktoplysninger og opdateringer af politikken.</p>
          </section>

          <section id="da-partners">
            <div className="section-label">Afsnit 1</div>
            <h2>For Vores Forretningspartnere</h2>

            <h3>Indgåelse og opfyldelse af kontrakt</h3>
            <div className="card">
              <p>Hvis du repræsenterer en virksomhed, vi samarbejder med, behandler vi personoplysninger for at understøtte forretningsforholdet, herunder kommunikation om produkter og services.</p>
              <p><strong>Retsgrundlag:</strong> Kontraktopfyldelse og vores legitime interesse i at drive forretning.</p>
              <p><strong>Oplysninger vi kan behandle:</strong> navn, kontaktoplysninger, stilling, onboarding-oplysninger for brugeradgange, IT-relaterede oplysninger (brugernavn, system- og applikationslogs, metadata), e-mailkorrespondance og anden kommunikationsdata.</p>
              <p><strong>Opbevaring:</strong> Så længe forretningsforholdet består og i op til tre (3) år derefter for at lette eventuel genoptagelse af samarbejdet. Visse data kan opbevares længere af juridiske grunde.</p>
            </div>

            <h3>Kundesupport</h3>
            <div className="card">
              <p>Når du kontakter vores support, behandler vi oplysninger såsom navn, virksomhed, e-mail, telefonnummer, rolle, evt. profilbillede og sagsdata.</p>
              <p><strong>Formål:</strong> At kommunikere med dig og dokumentere behandlingen af supporthenvendelser.</p>
              <p><strong>Retsgrundlag:</strong> Vores legitime interesse i at yde kundeservice og udvikle vores forretning.</p>
              <p><strong>Opbevaring:</strong> I kontraktperioden og en kort periode herefter.</p>
            </div>

            <h3>Markedsføring</h3>
            <div className="card">
              <p>Vi markedsfører vores produkter over for nuværende og potentielle kunder. Hvis du repræsenterer en virksomhed, vi samarbejder med eller ønsker at samarbejde med, kan vi behandle dine kontaktoplysninger til markedsføring via e-mail, SMS eller sociale medier (fx LinkedIn).</p>
              <p><strong>Oplysninger vi kan behandle:</strong> præferencer, enhedsdata, IP-adresse og browseradfærd — med henblik på at tilpasse vores kommunikation.</p>
              <p>Vi anvender ikke automatiske afgørelser. Du kan til enhver tid frabede dig profilering ved at kontakte os.</p>
              <p><strong>Opbevaring:</strong> Indtil du framelder dig, eller op til ét (1) år efter seneste aktivitet.</p>
            </div>

            <h3>Opfyldelse af juridiske forpligtelser</h3>
            <div className="card">
              <p>Vi kan behandle personoplysninger for at overholde bogførings-, regnskabs- og databeskyttelseslovgivning.</p>
              <p><strong>Retsgrundlag:</strong> Lovkrav og vores legitime interesse i at dokumentere håndterede forespørgsler.</p>
              <p><strong>Opbevaring:</strong> I den periode, lovgivningen kræver.</p>
            </div>

            <h3>Retlige krav</h3>
            <div className="card">
              <p>I forbindelse med eventuelle tvister kan vi behandle personoplysninger for at fastlægge, gøre gældende eller forsvare juridiske krav.</p>
              <p><strong>Retsgrundlag:</strong> Vores legitime interesse i at gøre gældende eller forsvare retskrav.</p>
              <p><strong>Opbevaring:</strong> Op til ti (10) år, men aldrig længere end nødvendigt.</p>
            </div>
          </section>

          <section id="da-prospects">
            <div className="section-label">Afsnit 2</div>
            <h2>For Potentielle Kunder &amp; Samarbejdspartnere</h2>
            <h3>Markedsføring</h3>
            <div className="card">
              <p>Som andre virksomheder har vi en legitim interesse i at markedsføre vores produkter til virksomheder, vi ønsker at samarbejde med. Vi kan indsamle oplysninger fra dig, din virksomhed eller offentlige kilder (fx CVR-registret) og dataleverandører.</p>
              <p>Vi kan kontakte dig via e-mail, SMS eller sociale medier (fx LinkedIn).</p>
              <p><strong>Oplysninger vi kan behandle:</strong> præferencer, enhedsdata, IP-adresse og browseradfærd med henblik på at målrette vores kommunikation effektivt.</p>
              <p><strong>Opbevaring:</strong> Så længe de er relevante, og op til to (2) år efter seneste kontakt. Hvis kontakten udvikler sig til et samarbejde, behandles data herefter som beskrevet under afsnit 1.</p>
            </div>

            <h3>Retlige krav</h3>
            <div className="card">
              <p>I forbindelse med eventuelle tvister kan vi behandle personoplysninger for at fastlægge, gøre gældende eller forsvare juridiske krav.</p>
              <p><strong>Opbevaring:</strong> Op til ti (10) år, men aldrig længere end nødvendigt.</p>
            </div>
          </section>

          <section id="da-sharing">
            <div className="section-label">Afsnit 3</div>
            <h2>Hvem Deler Vi Dine Oplysninger Med?</h2>
            <p>Svendetid kan benytte eksterne tjenesteudbydere, der fungerer som databehandlere — fx til hosting, support, markedsføring og it-drift. Disse udbydere kan få adgang til og behandle dine personoplysninger, men er kontraktligt forpligtede til at beskytte oplysningerne og kun handle på vores dokumenterede instruktioner.</p>
            <p>Vi sælger ikke dine personoplysninger til tredjeparter.</p>
          </section>

          <section id="da-storage">
            <div className="section-label">Afsnit 4</div>
            <h2>Hvor Opbevares Dine Oplysninger?</h2>
            <p>Vi bestræber os på at opbevare personoplysninger inden for EU/EØS. Hvis data overføres uden for EU/EØS, sikrer vi et tilstrækkeligt beskyttelsesniveau gennem EU-Kommissionens standardkontraktbestemmelser eller andre godkendte overførselsmekanismer i overensstemmelse med GDPR kapitel V.</p>
          </section>

          <section id="da-rights">
            <div className="section-label">Afsnit 5</div>
            <h2>Dine Rettigheder</h2>
            <p>Som registreret har du følgende rettigheder i henhold til GDPR. For at gøre brug af dem bedes du kontakte os (se afsnit 8).</p>
            <div className="rights-grid">
              <div className="right-item">Ret til indsigt i de oplysninger, vi behandler om dig</div>
              <div className="right-item">Ret til berigtigelse af urigtige eller ufuldstændige oplysninger</div>
              <div className="right-item">Ret til sletning ("retten til at blive glemt"), hvor der ikke er lovligt grundlag for opbevaring</div>
              <div className="right-item">Ret til begrænsning af behandling i visse tilfælde</div>
              <div className="right-item">Ret til dataportabilitet</div>
              <div className="right-item">Ret til at gøre indsigelse mod behandling baseret på legitim interesse</div>
              <div className="right-item">Ret til at trække samtykke tilbage, hvis behandlingen er baseret på samtykke</div>
              <div className="right-item">Ret til at klage til en tilsynsmyndighed</div>
            </div>
            <p style={{ marginTop: 18 }}>Ønsker du at klage, kan du kontakte Datatilsynet: <a href="https://www.datatilsynet.dk" style={{ color: "var(--brand)" }}>www.datatilsynet.dk</a></p>
          </section>

          <section id="da-security">
            <div className="section-label">Afsnit 6</div>
            <h2>Datasikkerhed</h2>
            <p>Svendetid implementerer passende tekniske og organisatoriske sikkerhedsforanstaltninger for at beskytte dine personoplysninger mod uautoriseret adgang, videregivelse, ændring eller destruktion. Disse foranstaltninger gennemgås og opdateres løbende i overensstemmelse med aktuel bedste praksis.</p>
          </section>

          <section id="da-contact">
            <div className="section-label">Afsnit 7</div>
            <h2>Kontakt Os</h2>
            <div className="contact-box">
              <p style={{ marginBottom: 10, color: "#334155" }}>Spørgsmål til denne politik eller ønsker du at udøve dine rettigheder?</p>
              <a href="mailto:privacy@svendetid.com">privacy@svendetid.com</a>
              <p style={{ marginTop: 10, fontSize: 14, color: "var(--muted)" }}>Svendetid · Danmark</p>
            </div>
          </section>

          <section id="da-changes">
            <div className="section-label">Afsnit 8</div>
            <h2>Ændringer i Privatlivspolitikken</h2>
            <p>Svendetid forbeholder sig retten til at ændre denne privatlivspolitik til enhver tid. Datoen for seneste ændring fremgår øverst på siden. Ved væsentlige ændringer vil vi give dig besked via platformen eller e-mail, hvor det er relevant.</p>
          </section>
        </div>
      </div>
      <LandingFooter lang={locale === "da" ? "da" : "en"} daHref="/privacy" enHref="/en/privacy" />
    </div>
  );
};

export default PrivacyPage;
